import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Edit3 } from "lucide-react";
import { api } from "../../lib/api";
import MediaPicker from "../../components/MediaPicker";
import MultiMediaPicker from "../../components/MultiMediaPicker";

/**
 * Generic CRUD table for admin.
 * Props:
 *  resource: string (path suffix — /admin/<resource>)
 *  publicPath: string (public listing path — /<publicPath>) with ?all=true
 *  title: string
 *  fields: [{ key, label, type: 'text'|'textarea'|'number'|'checkbox'|'select'|'array', options?, span? }]
 *  defaults: object
 */
export default function CrudResource({ resource, publicPath, title, fields, defaults }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null|new|item
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = () => api.get(`/${publicPath}?all=true`).then((r) => setItems(r.data));

  useEffect(() => { load(); }, [publicPath]);

  const openNew = () => { setForm({ ...defaults }); setEditing("new"); };
  const openEdit = (it) => { setForm({ ...it }); setEditing(it.id); };
  const close = () => { setEditing(null); setForm({}); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post(`/admin/${resource}`, form);
        toast.success("Eklendi");
      } else {
        await api.put(`/admin/${resource}/${editing}`, form);
        toast.success("Güncellendi");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/admin/${resource}/${id}`);
      toast.success("Silindi");
      setConfirmId(null);
      load();
    } catch (e) {
      toast.error("Silinemedi");
    } finally {
      setBusy(false);
    }
  };

  const bind = (k) => ({
    value: form[k] ?? "",
    onChange: (e) => {
      const v = e.target.type === "checkbox" ? e.target.checked
        : e.target.type === "number" ? Number(e.target.value)
        : e.target.value;
      setForm((f) => ({ ...f, [k]: v }));
    },
  });

  const inputCls = "w-full bg-[#0A0A11] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#A855F7]";

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tight text-white">{title}</h1>
          <p className="text-white/50 text-sm mt-1">Toplam {items.length} kayıt</p>
        </div>
        <button data-testid="crud-new" onClick={openNew} className="btn-primary text-[11px]">
          <Plus size={14} /> Yeni Ekle
        </button>
      </div>

      <div className="rounded-xl border border-white/5 bg-[#101018] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-white/5">
            <tr className="text-white/50 text-[10px] uppercase tracking-widest">
              {fields.slice(0, 4).map((f) => (
                <th key={f.key} className="text-left px-4 py-3 font-medium">{f.label}</th>
              ))}
              <th className="text-right px-4 py-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`row-${it.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                {fields.slice(0, 4).map((f) => (
                  <td key={f.key} className="px-4 py-3 text-white/80 max-w-[300px] truncate">
                    {f.type === "checkbox" ? (it[f.key] ? "✓" : "—") : String(it[f.key] ?? "—")}
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="inline-flex items-center gap-1 text-white/70 hover:text-[#A855F7] px-2 py-1 text-xs">
                    <Edit3 size={12} /> Düzenle
                  </button>
                  <button onClick={() => setConfirmId(it.id)} data-testid={`delete-${it.id}`} className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 px-2 py-1 text-xs">
                    <Trash2 size={12} /> Sil
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="text-center text-white/40 py-10">Kayıt yok.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit / New modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={close}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-auto rounded-2xl border border-white/10 bg-[#101018] p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl uppercase text-white">
                {editing === "new" ? "Yeni" : "Düzenle"}
              </h2>
              <button onClick={close} className="text-white/50 hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {fields.map((f) => (
                <label key={f.key} className={`block ${f.span === 2 ? "col-span-2" : ""}`}>
                  <span className="text-[10px] tracking-widest uppercase text-white/50 font-mono-ui mb-1 block">{f.label}</span>
                  {f.type === "textarea" ? (
                    <textarea rows={4} data-testid={`field-${f.key}`} className={inputCls} {...bind(f.key)} />
                  ) : f.type === "checkbox" ? (
                    <input type="checkbox" data-testid={`field-${f.key}`} checked={!!form[f.key]} onChange={(e) => setForm((fx) => ({ ...fx, [f.key]: e.target.checked }))} />
                  ) : f.type === "select" ? (
                    <select data-testid={`field-${f.key}`} className={inputCls} {...bind(f.key)}>
                      <option value="">—</option>
                      {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : f.type === "array" ? (
                    <input
                      data-testid={`field-${f.key}`}
                      type="text"
                      value={(form[f.key] || []).join(", ")}
                      onChange={(e) => setForm((fx) => ({ ...fx, [f.key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))}
                      placeholder="virgülle ayır"
                      className={inputCls}
                    />
                  ) : f.type === "media" ? (
                    <MediaPicker
                      value={form[f.key] || ""}
                      accept={f.accept || "both"}
                      onChange={(u) => setForm((fx) => ({ ...fx, [f.key]: u }))}
                    />
                  ) : f.type === "media-array" ? (
                    <MultiMediaPicker
                      value={form[f.key] || []}
                      accept={f.accept || "image"}
                      onChange={(arr) => setForm((fx) => ({ ...fx, [f.key]: arr }))}
                    />
                  ) : (
                    <input type={f.type || "text"} data-testid={`field-${f.key}`} className={inputCls} {...bind(f.key)} />
                  )}
                </label>
              ))}
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={close} className="btn-ghost text-[11px]">İptal</button>
              <button data-testid="crud-save" onClick={save} disabled={busy} className="btn-primary text-[11px]">
                <Save size={14} /> Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setConfirmId(null)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#101018] p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white text-lg mb-2">Silmek istediğine emin misin?</h3>
            <p className="text-white/50 text-sm mb-6">Bu işlem geri alınamaz.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmId(null)} className="btn-ghost text-[11px]">Vazgeç</button>
              <button data-testid="confirm-delete" onClick={() => del(confirmId)} disabled={busy} className="btn-primary text-[11px] bg-red-500 border-red-500">
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
